import { addNewRelicPageAction } from 'ddc-new-relic';
import isObject from './isObject';
import getDotNotationFromString from './getDotNotationFromString';
import putInArrayIfNotArray from './putInArrayIfNotArray';
import create from './create';
import createNestedObject from './createNestedObject';
import scripts from './scripts';
import {
	getPreviousData,
	saveDDCDataLayer,
	savePrevEventData
} from './previousData';
import vehicleDataUpdated from './vehicleDataUpdated';
import logIfNotInTestSuite from './logIfNotInTestSuite';
import mutateItem from './mutateItem';
import objectMutator from './objectMutator';
import formatSlugs from './formatSlugs';
import replaceSlugs from './replaceSlugs';
import getReplacement from './getReplacement';
import userShouldBeTracked from './userShouldBeTracked';
import copyPayload from './copyPayload';
import reportToEventViewer from './reportToEventViewer';
import getEnv from './getEnv';
import cloneObject from './cloneObject.js';
import executeObjectFunctions from './executeObjectFunctions.js';
import nrSwitcherOn from './nrSwitcher';
import jsonStringifyWithFunctions from './jsonStringifyWithFunctions';

const uuidsShown = new Set();
let flags;

const VEHICLE_ATTRS = [
	'accountId',
	'askingPrice',
	'autodataCaId',
	'bodyStyle',
	'certified',
	'chromeId',
	'cityFuelEconomy',
	'classification',
	'dealerCodes',
	'doors',
	'driveLine',
	'engine',
	'engineSize',
	'exteriorColor',
	'finalPrice',
	'fuelType',
	'highestPrice',
	'highwayFuelEconomy',
	'images',
	'interiorColor',
	'internetPrice',
	'inventoryType',
	'link',
	'make',
	'model',
	'modelCode',
	'msrp',
	'odometer',
	'optionCodes',
	'retailValue',
	'salePrice',
	'startingPrice',
	'status',
	'stockNumber',
	'transmission',
	'trim',
	'uuid',
	'vin',
	'year'
];

/**
 *
 * @param {Object} vehicle A vehicle object to be converted to the WIAPI vehicle spec
 * @returns A vehicle object matching the WIAPI vehicle spec.
 *
 * Currently supports an object where the keys are lowercase versions of what the WIAPI returns.
 * An address, dealerCodes, and images objects are created to match the WIAPI as well
 */
const getWIAPIVehicle = (win, vehicle) => {
	const newVehicle = {
		address: {
			accountName: undefined,
			city: vehicle?.city,
			state: vehicle?.state,
			street: vehicle?.address,
			postalCode: vehicle?.zipcode,
			country: undefined
		}
	};

	VEHICLE_ATTRS.forEach((attr) => {
		const value = vehicle?.[attr?.toLowerCase()];

		if (value && value !== 'null') {
			newVehicle[attr] = value;
		}
	});

	newVehicle.dealerCodes = win?.DDC?.dataLayer?.dealership?.dealerCode;
	newVehicle.images =
		vehicle?.images && Array.isArray(vehicle?.images)
			? vehicle?.images
			: vehicle?.images?.split(',');

	return newVehicle;
};

const pushToFirehose = (message, DDC, featureFlags) => {
	if (
		(message?.event?.startsWith('ddc.') ||
			['userEvent', 'specialsEvent', 'widgetUserEvent'].includes(
				message?.event
			)) &&
		/^[A-Za-z.]+$/.test(message?.event)
	) {
		let eventCategory = 'ddcEvent';
		let eventName = message.event;
		let payload = message;
		if (!message.event.startsWith('ddc.')) {
			eventCategory = message.event;
		}
		if (eventCategory === 'widgetUserEvent') {
			eventName = message.widgetName;
		} else if (eventCategory === 'userEvent') {
			eventName = message.eventAction;
		} else if (eventCategory === 'specialsEvent') {
			eventName = message.specialType;
		}
		if (message?.eventData?.vehicle) {
			const {
				eventData: {
					vehicle: {
						features: _features,
						images: _images,
						...restOfVehicle
					},
					...restOfEventData
				},
				...restOfMessage
			} = message;
			payload = {
				...restOfMessage,
				eventData: {
					...restOfEventData,
					vehicle: {
						images: [],
						...restOfVehicle
					}
				}
			};
		}
		if (
			eventCategory === 'ddcEvent' &&
			payload?.eventData &&
			typeof payload.eventData === 'object'
		) {
			/* If this is a ddc.x event, the real data is store in payload.eventData, flatten this for easier aggragation */
			payload = payload.eventData;
		}
		DDC.pushToFirehose(
			eventCategory,
			eventName,
			{
				...payload,
				...(typeof payload.element !== 'string' && { element: '' }),
				...(typeof payload.elementCTA !== 'string' && {
					elementCTA: ''
				})
			},
			featureFlags?.['ws-tagging-firehose'] || 1
		);
	}
};

/**
 * Creates a new MappingConfiguration
 * @class
 * @param {Object} configuration - A tracking project configuration.
 * @param {string} name - The name of the tracking project.
 */
class MappingConfiguration {
	constructor(configuration, name) {
		const {
			grouping,
			configuration: {
				create: objectOfCreation = false,
				lookups,
				previousData: previousDataConfig = {},
				sendingLogic = {}
			} = false,
			event: {
				events = false,
				triggerEvents = false,
				fireGroups = false
			} = false,
			utils = {},
			preview = false
		} = configuration;

		// allows looking up anything in DDC.WdigetData['ws-tagging'].mapping.xxxxxx
		// ex. [[mapping.pagealias.pageType]]
		// ex. [[mapping.form.init.SERVICE_APPT]]
		this.mapping = configuration;

		// THOUGHTS: should there be this.event.events, this.event.triggerEvents, this.event.fireGroups or keep like below?
		this.event = events;
		this.triggerEvents = triggerEvents;
		this.fireGroups = fireGroups;

		// this is the configuration for a tagging project
		// sendData, sharedPayloads, scripts, create, looksups are all things you will find in this
		this.configuration = configuration.configuration;

		this.name = name;
		this.grouping = grouping;
		this.prevData = getPreviousData(name, grouping, window);
		this.preview = preview;
		this.scriptsLoaded = new Set();
		this.sendingLogic = sendingLogic;
		this.lookups = lookups;
		this.utils = utils;
		this.triggeredEvents = new Map();
		this.firedGroups = new Map();

		const prevEventDataKey = `wsTaggingPrevEvents${name}${grouping}`;
		const prevEventData = JSON.parse(
			localStorage.getItem(prevEventDataKey)
		);
		this.mapping.prevEventData = prevEventData;
		localStorage.removeItem(prevEventDataKey);

		// don't create things when in preview mode
		if (!preview && objectOfCreation) {
			create(objectOfCreation, window);
		}

		saveDDCDataLayer(
			previousDataConfig?.ddcDataLayer,
			name,
			grouping,
			window
		);
	}

	/**
	 *
	 * @param {Object} replacement Current replacement value at time of function call
	 * @returns string
	 * @description Mutate every value of an object with globalPayloadMutators
	 */
	handleGlobalPayloadMutators(replacement) {
		const mutators = this.configuration?.globalPayloadMutators;
		return mutators
			? objectMutator(replacement, mutateItem.bind(undefined, mutators))
			: replacement;
	}

	/**
	 *
	 * @param {Object} logic - Object that includes the mustNotInclude and/or mustNotExist and/or mustInclude objects.
	 * The dot notation string follows the slug logic.
	 * @param {Object} eventData - The data from the current event
	 * @returns {boolean}
	 * @example
	 *
	 * logic:
	 * {
	 * 	mustInclude|mustIncludeOr|mustNotInclude|mustNotEqual|mustEqual|mustEqualOr: {
	 * 	 'slugVal': 'testVal'
	 *  }
	 * }
	 *
	 * @description This method returns:
	 * mustInclude && mustIncludeOneOf:
	 *  slugVal is undefined: false
	 *  testVal is *: true
	 *  slugVal includes testVal: true
	 * mustNotInclude:
	 *  slugVal is undefined: true
	 *  slugVal and testVal are '': false
	 *  testVal is an '': true
	 *  testVal is *: false
	 *  slugVal includes testVal: false
	 * mustNotEqual:
	 *  testVal is {{EMPTYSTRING}} and slugVal is '': false
	 *  slugVal is undefined and testVal is not '' and slugVal is not '': true
	 *  testVal is *: false
	 *  slugVal equals testVal: false
	 * mustEqual && mustEqualOneOf
	 *  testVal is {{EMPTYSTRING}} and slugVal is '': true
	 *  testVal is *: true
	 *  slugVal equals testVal: true
	 *  slugVal is undefined and testVal is undefined: true
	 *
	 * The 'or' versions of functions only require one of the items in the array to pass.
	 */
	testLogic = (logicValue, eventData) => {
		if (typeof logicValue === 'function') {
			return logicValue({
				eventData,
				mapping: this.mapping,
				utils: this.utils,
				lookups: this.lookups,
				flags
			});
		}
		const {
			mustEqual = false,
			mustEqualOneOf = false,
			mustInclude = false,
			mustIncludeOneOf = false,
			mustNotEqual = false,
			mustNotInclude = false
		} = logicValue;
		let result = true;

		if (
			mustEqual ||
			mustEqualOneOf ||
			mustInclude ||
			mustIncludeOneOf ||
			mustNotEqual ||
			mustNotInclude
		) {
			const test = (entries, type) => {
				return (
					!entries ||
					Object.entries(entries).every(
						([testKey, testValMaybeArray]) => {
							/*
								// support testing an array of items
								"mustNotInclude": {
									"eventData.url": ["cool","site"]
								}
							*/
							const testValArr = !Array.isArray(testValMaybeArray)
								? [testValMaybeArray]
								: testValMaybeArray;

							const testValArrType =
								mustEqualOneOf || mustIncludeOneOf
									? 'some'
									: 'every';

							return testValArr[testValArrType]((testVal) => {
								const slugVal = getReplacement({
									str: testKey,
									eventData,
									mapping: this.mapping,
									utils: this.utils,
									lookups: this.lookups,
									flags
								});

								let pass = true;
								const slugValUndefined =
									typeof slugVal === 'undefined';
								const wildcard = testVal === '*';
								const testValEmptyString = testVal === '';
								const slugValEmptyString = slugVal === '';
								const emptyStringMatch =
									testValEmptyString && slugValEmptyString;

								if (type === 'include') {
									if (slugValUndefined) {
										pass = false;
									} else {
										pass =
											wildcard ||
											String(slugVal).includes(testVal);
									}
								} else if (type === 'notinclude') {
									if (slugValUndefined) {
										pass = true;
									} else if (emptyStringMatch) {
										pass = false;
									} else if (testValEmptyString) {
										pass = true;
									} else {
										pass =
											!wildcard &&
											!String(slugVal).includes(testVal);
									}
								} else if (type === 'notequal') {
									if (
										testVal === '{{EMPTYSTRING}}' &&
										slugVal === ''
									) {
										pass = false;
									} else if (
										slugValUndefined &&
										!testValEmptyString &&
										!emptyStringMatch
									) {
										pass = true;
									} else {
										pass = !wildcard && slugVal !== testVal;
									}
								} else if (type === 'equal') {
									if (
										testVal === '{{EMPTYSTRING}}' &&
										slugVal === ''
									) {
										pass = true;
									} else {
										pass = wildcard || slugVal === testVal;
									}
								}

								return pass;
							});
						}
					)
				);
			};

			result =
				test(mustEqual, 'equal') &&
				test(mustEqualOneOf, 'equal') &&
				test(mustNotInclude, 'notinclude') &&
				test(mustNotEqual, 'notequal') &&
				test(mustInclude, 'include') &&
				test(mustIncludeOneOf, 'include');
		}

		return result;
	};

	/**
	 *
	 * @param {Object} logic - Logic rule from the event configuration
	 * @param {Object} eventData - The data from the current event
	 * @returns {boolean} Whether the rules passes or not.
	 */
	testRule(logic, eventData = {}) {
		const {
			mustExist = false,
			// we do not care about the next three but do not want them in the properties
			// _ avoids linter error
			mustEqual: _mustEqual,
			mustInclude: _mustInclude,
			mustNotEqual: _mustNotEqual,
			mustNotInclude: _mustNotInclue,
			...properties
		} = logic;

		return (
			(!mustExist ||
				putInArrayIfNotArray(mustExist).every(
					(test) =>
						typeof replaceSlugs({
							input: test,
							eventData,
							mapping: this.mapping,
							utils: this.utils,
							lookups: this.lookups,
							flags
						}) !== 'undefined'
				)) &&
			this.testLogic(logic, eventData) &&
			Object.entries(properties).every(
				([key, value]) => {
					/*
					Support logic like:
					{
						'eventData.attributes.data-phone-ref': 'SALES'
					}
					*/
					const replacement = getReplacement({
						str: key,
						eventData,
						mapping: this.mapping,
						utils: this.utils,
						lookups: this.lookups,
						flags
					});

					// this allows testing of boolean false
					if (
						typeof replacement === 'boolean' &&
						typeof value === 'boolean'
					) {
						return replacement === value;
					} else {
						const test = replacement || eventData[key];

						if (typeof test === 'string') {
							return test?.includes(value);
						} else if (typeof test === 'boolean') {
							return test === value;
						}

						return false;
					}
				}
				// if the mapping is set to look for something that doesn't exist in the eventData the optional chaining prevents a js error

				/*
					logic: {
						'eventData.attributes.data-phone-ref': 'SALES'
					}
				*/
			)
		);
	}

	/**
	 *
	 * @param {String} fireGroup - The group the event is part of
	 * @returns Whether the fireGroup meets the criteria to pass
	 *  If there is no count for the fireGroup then returns true and message logged
	 *  If count is zero then returns true and message logged
	 *  If count > 0 then true returned if fireGroup tally is less than the count
	 *  Side effect: function tallys fireGroup count for use in this function
	 */
	testFireGroup(fireGroup) {
		let fireGroupPass = true;
		if (fireGroup) {
			const operationsToRun = Object.keys(this?.fireGroups?.[fireGroup]);
			/* Update to support an array of operations when we support triggering from here. */
			switch (operationsToRun[0]) {
				case undefined: {
					logIfNotInTestSuite({
						msg: 'An operation is required when using a fireGroup'
					});
					break;
				}
				case 'maxCount': {
					const maxCount = this?.fireGroups?.[fireGroup]?.maxCount;
					if (maxCount === 0) {
						logIfNotInTestSuite({
							msg: 'maxCount can not be zero'
						});
					} else {
						const firedGroupCount = this.firedGroups.get(fireGroup);

						if (!firedGroupCount) {
							this.firedGroups.set(fireGroup, 1);
						} else if (firedGroupCount < maxCount) {
							this.firedGroups.set(
								fireGroup,
								firedGroupCount + 1
							);
						} else {
							fireGroupPass = false;
						}
					}
					break;
				}
				case 'minCount': {
					const minCount = this?.fireGroups?.[fireGroup]?.minCount;
					if (minCount === 0) {
						logIfNotInTestSuite({
							msg: 'minCount can not be zero'
						});
					} else {
						const firedGroupCount = this.firedGroups.get(fireGroup);

						if (firedGroupCount >= minCount) {
							fireGroupPass = true;
						} else if (!firedGroupCount) {
							fireGroupPass = false;
							this.firedGroups.set(fireGroup, 1);
						} else {
							fireGroupPass = false;
							this.firedGroups.set(
								fireGroup,
								firedGroupCount + 1
							);
						}
					}
					break;
				}
				default: {
					break;
				}
			}

			if (fireGroupPass && operationsToRun.length) {
				window.dispatchEvent(
					new CustomEvent('wsTaggingRuleMatch', {
						detail: {
							data: fireGroup,
							type: 'fireGroup'
						}
					})
				);
			}
		}

		return fireGroupPass;
	}

	/**
	 *
	 * @param {Object} eventData - The data from the current event
	 * @param {Object} rules - Object of a specific events rule or array of rules and their payloads w/ associated logic
	 *  and whatever else we put in there
	 * @returns The rule that has the payload to send to the third party or undefined if no rule found or one should not be sent
	 * @example
	 * // possible return value:
	 * {
	 * 	logic: {...},
	 * 	payload: {...}
	 * }
	 */
	getRule(eventData, rules) {
		let mutableMapping = cloneObject(rules);

		if (!Array.isArray(mutableMapping)) {
			// support simple mappings with no logic
			if (!mutableMapping.logic) {
				return mutableMapping;
			}

			// support simple mappings with logic
			mutableMapping = [mutableMapping];
		}

		// support mappings with multiple rules
		return mutableMapping.find((rule) => {
			const { fireGroup = false } = rule;

			const rulePass =
				rule.logic &&
				putInArrayIfNotArray(rule.logic).some((logic) =>
					this.testRule(logic, eventData)
				);
			const noLogic = !rule.logic && true; // && true ??? what's this?
			const fireGroupPass =
				(rulePass || noLogic) && this.testFireGroup(fireGroup);

			return (rulePass || noLogic) && fireGroupPass;
		});
	}

	/**
	 *
	 * @param {string} event - The DDC event that was triggered
	 * @param {string} clientEventName - What the client labels this event as. Not all clients/events will have a value for this.
	 * @param {Object} eventData - The eventData in the message.
	 * @param {Object} props - Extra attributes to send to NewRelic
	 * @param {Object} win - The window object. Passed in mainly to make testing cleaner.
	 * @param {String} sender - The sender (where the data is going window.utag for example)
	 */
	reportToNewRelic({
		clientEventName,
		event,
		eventData = {},
		sender = '',
		win = {},
		...props
	}) {
		if (
			userShouldBeTracked() &&
			nrSwitcherOn(flags, this.name, clientEventName || event)
		) {
			const pageTrackingTags =
				win?.DDC?.dataLayer?.page?.trackingTags || {};
			const pageTrackingTagsKeys =
				Object.keys(pageTrackingTags).toString();
			const pageTrackingTagsValues =
				Object.values(pageTrackingTags).toString();
			const eventTrackingTags = eventData?.trackingTags;

			addNewRelicPageAction(`${this.name}_send_data`, {
				eventName: clientEventName || event,
				...(eventTrackingTags && { eventTrackingTags }),
				mappingGroup: this.grouping,
				mappingSource: 'native ws-tagging',
				...(pageTrackingTagsKeys && {
					pageTrackingTags: pageTrackingTagsKeys
				}),
				...(pageTrackingTagsValues && { pageTrackingTagsValues }),
				...props,
				sourceEvent: event,
				wsTaggingPreview: this.preview,
				...(sender && { sender })
			});
		}
	}

	/**
	 *
	 * @param {string|Function|Object[]} sender - Sender can be:
	 * * string like 'bbb' or 'aaa.bbb' then 'bbb' is set to the payload
	 * * array then the payload is pushed into it
	 * * function then the function is called with the payload as the argument
	 * @param {Object} payload - The payload to send to the third party
	 * @param {string} senderString - The string from the configuration before
	 * @returns Whether or not something was sent.
	 * @description Sends an event to the client
	 */
	sendEvent(
		sender,
		payload,
		senderString,
		{
			addSourceToSecondArgument = false,
			addEventToSecondArgument = false,
			event = ''
		} = {}
	) {
		let sent = false;

		try {
			if (typeof sender === 'function') {
				const args = [payload];
				const secondArg = {};
				if (addSourceToSecondArgument) {
					secondArg.source = 'ws-tagging';
				}
				if (addEventToSecondArgument) {
					secondArg.event = event;
				}
				if (Object.keys(secondArg).length) {
					args.push(secondArg);
				}
				sender(...args);
				sent = true;
			} else if (Array.isArray(sender)) {
				sender.push(payload);
				sent = true;
			} else if (typeof sender === 'string') {
				createNestedObject(window, sender, payload);
				sent = true;
			}
		} catch (error) {
			logIfNotInTestSuite({
				msg: `Error sending ${senderString}: ${error.message}`
			});
		}

		if (sent) {
			logIfNotInTestSuite({
				msg: `Sending payload for ${this.name} to the ${
					typeof sender === 'string' ? 'object' : typeof sender
				} ${senderString}`
			});
		}

		return sent;
	}

	resolveClientEventName({ clientEventName, eventData, payload }) {
		return (
			payload?.[clientEventName] ||
			formatSlugs({
				input: clientEventName,
				eventData,
				configuration: this.configuration,
				utils: this.utils,
				mapping: this.mapping,
				flags,
				payload
			}) ||
			eventData.event
		);
	}

	sendEvents(
		payloadIn,
		event,
		excludedSender = '',
		eventData = {},
		senderOverride
	) {
		const { reporting: { clientEventName = undefined } = false } =
			this.configuration;

		// support a custom sender for the event rule
		// added to array as sendData supports an array of senders
		const sendData = senderOverride
			? [
					{
						sender: senderOverride
					}
			  ]
			: this.configuration.sendData;

		// Convert single payloads into an array
		// This supports a payload being an array of payloads
		const payloads = Array.isArray(payloadIn) ? payloadIn : [payloadIn];
		let reportsForEventViewer = [];
		let reportsForNewRelic = [];

		payloads.forEach(async (payload) => {
			const data = copyPayload(payload, eventData);
			const resolvedClientEventName = this.resolveClientEventName({
				clientEventName,
				eventData,
				payload: data
			});

			// if no senderOverride and
			// if a sending util is found then sending is handled there
			if (!senderOverride && this?.utils?.sending) {
				const {
					reportsForEventViewer: reportForEventViewer,
					reportsForNewRelic: reportForNewRelic = {}
				} = this.utils.sending({
					data,
					event,
					eventData,
					flags,
					...(this.utils.groupingSending && {
						groupingSending: this.utils.groupingSending
					}),
					mapping: this.mapping,
					preview: this.preview,
					win: window
				});

				reportsForEventViewer =
					reportsForEventViewer.concat(reportForEventViewer);
				reportsForNewRelic =
					reportsForNewRelic.concat(reportForNewRelic);
			} else {
				sendData.forEach(
					({
						sender: senderString,
						report = true,
						eventViewer = true,
						addEventToSecondArgument = false,
						addSourceToSecondArgument = false
					}) => {
						if (eventViewer) {
							reportsForEventViewer.push({
								payload: data,
								sender: senderString,
								clientEventName: resolvedClientEventName
							});
						}

						// the commas ensure exact matches of what is excluded
						if (
							!`,${excludedSender},`.includes(`,${senderString},`)
						) {
							if (report) {
								reportsForNewRelic.push({
									clientEventName: resolvedClientEventName,
									sender: senderString
								});
							}

							const sender = getDotNotationFromString(
								window,
								senderString
							);

							if (!this.preview) {
								// next bit of logic got messy trying to handle senders being functions, arrays, objects
								// if sender is an object we pass the sender string
								// otherwise we pass the function/array to call/push to

								const args = [
									isObject(sender) ? senderString : sender,
									copyPayload(payload, eventData),
									senderString
								];

								if (
									addEventToSecondArgument ||
									addSourceToSecondArgument
								) {
									args.push({
										addEventToSecondArgument,
										addSourceToSecondArgument,
										event
									});
								}

								this.sendEvent(...args);
							}
						} else {
							logIfNotInTestSuite({
								msg: `Skipping excluded ${senderString} for ${this.name}`
							});
						}
					}
				);
			}
		});

		reportsForNewRelic.forEach(async (report) => {
			const { clientEventName: reportClientEventName, sender } =
				await report;

			this.reportToNewRelic({
				...(reportClientEventName && {
					clientEventName: reportClientEventName
				}),
				event,
				eventData,
				sender,
				win: window
			});
		});

		reportsForEventViewer.forEach(async (report) => {
			if (report) {
				const {
					payload,
					clientEventName: reportClientEventName,
					...additionalAttributes
				} = await report;

				if (payload) {
					reportToEventViewer({
						...(reportClientEventName && {
							clientEventName: reportClientEventName
						}),
						event,
						payload,
						project: this.name,
						...additionalAttributes
					});
				}
			}
		});
	}

	/**
	 *
	 * @param {Object} payload - A payload with a 'shared' object
	 * @param {Object} eventData - The current events data
	 * @param {Object} rule
	 * @param {Object} event
	 * @returns A new object with only the specific 'shared' part resolved
	 */
	reduceSharedPayload = (payloadIn, eventData, rule, event) => {
		const payloads = Array.isArray(payloadIn) ? payloadIn : [payloadIn];

		const resolvedPayloads = payloads.map((payload) => {
			const { shared = [], ...rest } = payload;
			return shared.reduce(
				(partialObject, currentItem) => {
					let mergedObject;

					// conditional shared payload
					if (currentItem?.logic && currentItem?.payload) {
						if (this.testRule(currentItem.logic, eventData)) {
							// in an events shared payload array each passing rule overwrites previous things
							mergedObject = {
								...partialObject,
								...currentItem.payload
							};
							window.dispatchEvent(
								new CustomEvent('wsTaggingRuleMatch', {
									detail: {
										data: jsonStringifyWithFunctions({
											event,
											...rule,
											sharedBlock: currentItem.logic
										}),
										type: 'rule'
									}
								})
							);
						} else {
							mergedObject = partialObject;
						}
					} else {
						// the non-shared items of the payload overwrite the shared items
						mergedObject = { ...currentItem, ...partialObject };
					}

					return mergedObject;
				},
				{ ...rest }
			);
		});

		return Array.isArray(payloadIn)
			? resolvedPayloads
			: resolvedPayloads[0];
	};

	/**
	 *
	 * @param {Object} payload - The events payload which should have shared items resolved
	 * @param {Object} eventData - The current events data
	 * @returns A new object with the shared pieces resolved
	 * @example
	 * "payload": {
	 * 		"shared": [
	 * 			// anything in the configuration sharedPayload pageView will be
	 * 			// overwritten by succeeding items in the events shared array
	 * 			"[[mapping.configuration.sharedPayloads.pageView]]",
	 * 			{
	 * 				"logic": {
	 * 					"eventData.isModal": true
	 * 				},
	 * 				"payload": {
	 * 					"dialog": true
	 * 				}
	 * 			}
	 * 		],
	 * 		// overwrites any of the shared items
	 * 		"nonSharedItem": 'nonSharedItem'
	 * }
	 */
	resolveSharedPayload = (payloadIn, eventData, rule, event) => {
		const payloads = Array.isArray(payloadIn) ? payloadIn : [payloadIn];

		const resolvedPayloads = payloads.map((payloadInIn) => {
			const payload = payloadInIn;
			// we could create a recursive function to traverse as many levels of nestedness as needed,
			// for now, maybe just to go one more level is enough
			Object.keys(payload).forEach((key) => {
				if (typeof payload[key] === 'object' && payload[key]?.shared) {
					payload[key] = this.reduceSharedPayload(
						payload[key],
						eventData,
						rule,
						event
					);
				}
			});

			return this.reduceSharedPayload(payload, eventData, rule, event);
		});

		return Array.isArray(payloadIn)
			? resolvedPayloads
			: resolvedPayloads[0];
	};

	/**
	 *
	 * @param {string} whenArg - When the script should be processed.
	 * @param {string} triggerArg - What triggers the script processing?
	 * @param {string} eventArg  - What event should the script be added during?
	 * @description The three arguments to this function correspond with where this function is called in the dataMapper.
	 * If you want a script to be processed in a particular place you set up the configuration scripts object with the same
	 * arguments that this function is called with. You can see an example of this in the Blast/Genesis configuration.
	 *
	 * If multiple scripts are configured to be loaded they happen in the order configured.
	 * Each script is also fully loaded before the next one loads.
	 */
	async processScripts(whenArg, triggerArg, eventArg) {
		const processScriptsInfo = {
			error: false,
			message: '',
			messages: []
		};

		if (this.configuration.scripts) {
			const { when, trigger, event, configs } =
				this.configuration.scripts;

			if (
				whenArg === when &&
				triggerArg === trigger &&
				eventArg === event
			) {
				// can't use for..of as eslint has some big warning
				// also get warning for for loop but disabling the eslint rule for reasons below
				for (let i = 0; i < configs.length; i++) {
					const { name, src, srcNp, type } = configs[i];
					if (!name || !src || !type) {
						processScriptsInfo.error = true;
						processScriptsInfo.message = `${this.name} ${this.grouping} script not configured correctly:`;
						logIfNotInTestSuite({
							msg: processScriptsInfo.message
						});
						if (!name) {
							const message = 'must have a name';
							processScriptsInfo.messages.push(message);
							logIfNotInTestSuite({ msg: message });
						}
						if (!src) {
							const message = 'must have a src';
							processScriptsInfo.messages.push(message);
							logIfNotInTestSuite({ msg: message });
						}
						if (!type) {
							const message = 'must have a type';
							processScriptsInfo.messages.push(message);
							logIfNotInTestSuite({ msg: message });
						}
					} else if (!this.scriptsLoaded.has(name)) {
						// extra safety check to make sure we don't attempt loading a script twice
						const srcFromEnv =
							srcNp && getEnv() !== 'prod' ? srcNp : src;
						const sluglessSrc = replaceSlugs({
							input: srcFromEnv,
							eventData: undefined,
							mapping: this.mapping,
							utils: this.utils,
							lookups: this.lookups,
							flags
						});

						// don't load third party scripts in preview mode
						if (!this.preview) {
							// we need these to happen sequentially and for loop is the way to go
							// eslint-disable-next-line no-await-in-loop
							await scripts[type]({
								flags,
								src: sluglessSrc,
								win: window
							});
						}
						this.scriptsLoaded.add(name);
						window.dispatchEvent(
							new Event(
								`ws-tagging-${this.name}-${this.grouping}-script-${name}-loaded`
							)
						);
					}
				}
			}
		}

		return Promise.resolve(processScriptsInfo);
	}

	async handleRule(_rule, event, eventData) {
		const rule = _rule;
		if (rule) {
			window.dispatchEvent(
				new CustomEvent('wsTaggingRuleMatch', {
					detail: {
						data: jsonStringifyWithFunctions({
							event,
							...rule
						}),
						type: 'rule'
					}
				})
			);
		}

		if (rule && rule.payload) {
			await vehicleDataUpdated(window);

			rule.payload = executeObjectFunctions(rule.payload, {
				eventData,
				configuration: this.configuration,
				flags,
				mapping: this.mapping,
				utils: this.utils
			});

			// since rule.payload may change, we need to check again
			if (rule.payload) {
				// TODO: This formats the shared payload even if it will not be used due to shared payload logic
				// can we resolve shared payload first?
				let payload = formatSlugs({
					input: rule.payload,
					eventData,
					configuration: this.configuration,
					utils: this.utils,
					mapping: this.mapping,
					flags
				});

				payload = this.resolveSharedPayload(
					payload,
					eventData,
					rule,
					event
				);
				payload = this.handleGlobalPayloadMutators(payload);

				const script = this.sendingLogic?.waitFor?.script;
				const eventException = this.sendingLogic?.exception?.event;
				const senderOverride = rule?.sender;

				// Example use case of below logic: Except for ddc.page.view Genesis events require utag.js to have loaded
				if (
					script &&
					!this.scriptsLoaded.has(`${script}`) &&
					(!eventException || eventException !== event)
				) {
					const listener = () => {
						this.scriptsLoaded.add(script);
						this.sendEvents(
							payload,
							event,
							rule.excluded,
							eventData,
							senderOverride
						);
					};
					window.addEventListener(
						`ws-tagging-${this.name}-${this.grouping}-script-${script}-loaded`,
						listener,
						{
							once: true
						}
					);
				} else {
					this.sendEvents(
						payload,
						event,
						rule.excluded,
						eventData,
						senderOverride
					);
				}

				// script processing entry point
				await this.processScripts('after', 'event', event);
			}
		}
	}

	/**
	 *
	 * @param {String} triggerName - Name of the event to trigger
	 * @param {Object} eventData  - The current events data
	 * @returns A matching rule from the configured triggerEvents.
	 * undefined is returned if the triggerName is not found in the triggerEvents
	 * undefined is returned if the payload is missing in the triggeredEvent
	 * undefined is returned if the triggeredEvent does not have passing logic (see getRule)
	 * undefined is returned if the triggeredEvent has a count and the count has already been met
	 */
	getTriggeredRule(triggerName, eventData) {
		let ruleToReturn;

		// const {
		// 	logic,
		// 	payload,
		// 	sender,
		// 	configuration: { count } = {}
		// } = this.triggerEvents?.[triggerName] || {};
		const rule = this.triggerEvents?.[triggerName] || {};

		if (!this.triggerEvents?.[triggerName]) {
			logIfNotInTestSuite({
				msg: `${triggerName} not found in triggerEvents`,
				data: this.triggerEvents
			});
		} else if (!rule.payload) {
			logIfNotInTestSuite({
				msg: `payload must exist in the triggerEvent: ${triggerName}`
			});
		} else {
			// need to make sure logic passes in the rule
			const triggerRule = this.getRule(eventData, rule);

			if (triggerRule) {
				if (rule?.configuration?.count) {
					const triggeredEventCount =
						this.triggeredEvents.get(triggerName);

					if (!triggeredEventCount) {
						this.triggeredEvents.set(triggerName, 1);
						ruleToReturn = triggerRule;
					} else if (triggeredEventCount < rule.configuration.count) {
						this.triggeredEvents.set(
							triggerName,
							triggeredEventCount + 1
						);
						ruleToReturn = triggerRule;
					}
				} else {
					ruleToReturn = triggerRule;
				}

				if (ruleToReturn) {
					window.dispatchEvent(
						new CustomEvent('wsTaggingRuleMatch', {
							detail: {
								data: triggerName,
								type: 'trigger'
							}
						})
					);
				}
			}
		}

		return ruleToReturn;
	}

	/**
	 *
	 * @param {Object} message - A message recieved by our widget with the event and some data.
	 * @param {string} message.event - The event in the message.
	 * @param {Object} message.eventData - The eventData in the message.
	 * @description This method:
	 * * Determines if the configuration has a matching event.
	 * * Determines the payload to send.
	 * * Sends the payload to all senders in the configuration.
	 * * Scripts are processed 'after' an 'event' matching message.event.
	 */
	async listener(message) {
		const { event } = message;
		const rawEventData = [
			'userEvent',
			'thirdPartyUserEvent',
			'specialsEvent'
		].includes(message.event)
			? message
			: message.eventData;

		this.mapping.prevEventData = {
			...this.mapping.prevEventData,
			...savePrevEventData(
				this.configuration?.previousData?.events,
				this.name,
				this.grouping,
				event,
				rawEventData,
				window
			)
		};

		// the this.event?.['*'] matches event.js file which has rules for any event not yet matched
		// made for unite to pass all ASC stuff through
		const eventRules = this.event?.[event] || this.event?.['*'];

		if (eventRules) {
			const eventData = {
				...rawEventData,
				...(this.prevData && { prev: this.prevData }),
				// 1) No circular references
				// 2) No scraping the page or getting tricky with this!
				// 3) widgetUserEvent uses 'element' and it is a string
				// 4) Do the decoration of eventData in ws-tracking please
				element:
					typeof rawEventData.element === 'string'
						? rawEventData.element
						: '',
				...(rawEventData?.elementCTA && {
					elementCTA:
						typeof rawEventData?.elementCTA === 'string'
							? rawEventData.elementCTA
							: ''
				})
			};

			const rule = this.getRule(eventData, eventRules);

			// if there is no specific event that matches and the event.js has a match all event
			if (!this.event?.[event] && this.event?.['*']) {
				// dispatch an event so we know this rule was matched for the match all event (*)
				window.dispatchEvent(
					new CustomEvent('wsTaggingRuleMatch', {
						detail: {
							data: jsonStringifyWithFunctions({
								event: '*',
								...rule
							}),
							type: 'rule'
						}
					})
				);
			}

			const rulesToHandle = [rule];

			const triggers =
				(rule?.trigger && putInArrayIfNotArray(rule.trigger)) || [];

			triggers.forEach((triggerName) => {
				const triggeredRule = this.getTriggeredRule(
					triggerName,
					eventData
				);
				if (triggeredRule) {
					if (
						this.triggerEvents?.[triggerName]?.configuration
							?.when === 'first'
					) {
						rulesToHandle.unshift(triggeredRule);
					} else {
						rulesToHandle.push(triggeredRule);
					}
				}
			});

			// need these to happen in order
			for (let i = 0; i < rulesToHandle.length; i++) {
				// eslint-disable-next-line no-await-in-loop
				await this.handleRule(rulesToHandle[i], event, eventData);
			}
		}
	}
}

const mappingConfigurations = [];

/**
 *
 * @param {Object} message - A message recieved by our widget with the event and some data.
 * @param {string} message.event - The event in the message.
 * @param {Object} message.eventData - The eventData in the message.
 * @description Calls the listener function for each MappingConfiguration in mappingConfigurations with the message.
 */
const callListener = (message) =>
	(message.eventData ||
		['userEvent', 'thirdPartyUserEvent', 'specialsEvent'].includes(
			message.event
		)) &&
	mappingConfigurations.forEach((configuration) =>
		configuration.listener(message)
	);

/**
 *
 * @param {Object} mappings Object representing all tracking projects for the current site.
 * @description This method:
 * * Iterates through all properties (mappingConfiguration's) of mappings
 * * If mappingConfiguration.event and mappingConfiguration.configuration exist create a new MappingConfiguration class.
 * * The class is constructed with the mappingConfiguration and that mappingConfigurations name.
 * * This is then pushed into the mappingConfigurations array.
 */
const setMappingConfigurations = (mappings) => {
	Object.entries(mappings).forEach(([mappingName, mappingConfiguration]) => {
		if (
			mappingConfiguration?.event &&
			mappingConfiguration?.configuration
		) {
			mappingConfigurations.push(
				new MappingConfiguration(mappingConfiguration, mappingName)
			);
		}
	});

	// some projects need to have their rules run before other projects
	// if the configuration has a priority set then projects with the lowest priority get ordered first
	mappingConfigurations.sort(
		(
			{
				configuration: {
					priority: priorityA = 999 // really high number that we can bump if we ever reach (999 projects that have an order...don't think so)
				}
			},
			{ configuration: { priority: priorityB = 999 } }
		) => {
			return priorityA - priorityB;
		}
	);
};

const initVehicleShown = async (win) => {
	const call = ({ vehicle, vehicle: { uuid }, timestamp, type }) => {
		// only fire once per page
		if (uuid && !uuidsShown.has(uuid)) {
			uuidsShown.add(uuid);
			const eventId = win.DDC.dataLayer?.generateId
				? win.DDC.dataLayer?.generateId()
				: Math.trunc(Math.random() * 1000000000);
			callListener({
				event: 'vehicle-shown',
				eventData: {
					eventId,
					timestamp,
					type,
					vehicle
				}
			});
		}
	};

	// SRP
	const API =
		win?.DDC?.APILoader &&
		(await win?.DDC?.APILoader.create('ws-tagging vehicle-shown'));
	API?.subscribe(
		'vehicle-shown-v1',
		({ timestamp, type, payload: vehicle = {} } = {}) => {
			call({
				timestamp,
				type,
				vehicle
			});
		}
	);

	// Legacy VLP
	const observerCallback = (entries, hproduct) => {
		entries.forEach((entry) => {
			if (entry.isIntersecting) {
				call({
					timestamp: Date.now(),
					type: 'vehicle-shown-legacy-vlp',
					vehicle: getWIAPIVehicle(win, hproduct.dataset)
				});
			}
		});
	};

	document.querySelectorAll('.hproduct').forEach((hproduct) => {
		// if (hproduct) {
		const observer = new IntersectionObserver(
			(entries) => {
				observerCallback(entries, hproduct);
			},
			{
				threshold: 1
			}
		);
		observer.observe(hproduct);
		// }
	});
};

/**
 * @param {Object} flags An object of feature flags.
 * @description This method grabs mappings out of DDC.WidgetData['ws-tagging'] and sets up the mapping configurations.
 * If there are mappings it then sets up a DDC style event (ex. ddc.page.view) listener as well as a WidgetUserEvent listener.
 */
const init = async ({ flags: featureFlags } = {}) => {
	const { DDC } = window;
	const wsTagging = DDC.WidgetData['ws-tagging'];
	wsTagging.browserEventNames = [];

	const mappings = wsTagging?.mapping;
	// Check DDC widget data to see what mappingConfigurations are enabled.
	if (mappings) {
		setMappingConfigurations(mappings);

		if (mappingConfigurations.length > 0) {
			callListener({
				event: 'ddc.wstagging.first',
				eventData: {
					alias: DDC?.dataLayer?.page?.pageInfo?.pageName,
					eventId: DDC?.dataLayer?.generateId?.()
				}
			});

			// there seem to be cases where window.dataLayer is not defined at this point
			// - saw this in NR JS errors
			window.dataLayer = window.dataLayer || [];

			wsTagging.ascDataLayerHelper = new window.DataLayerHelper(
				window?.asc_datalayer?.events || [],
				(_dataLayer, message) => {
					callListener({
						event: message.event,
						eventData: message
					});
				}
			);

			wsTagging.dataLayerHelper = new window.DataLayerHelper(
				window.dataLayer || [], // if window.dataLayer is again undefined just send an empty array to avoid js errors
				// ignore widgetUserEvents from window.dataLayer pushes as the optionalData we might want is stripped out
				(dataLayer, message) => {
					if (
						message &&
						message.event &&
						message.event !== 'widgetUserEvent'
					) {
						callListener(message);
					}

					pushToFirehose(message, DDC, featureFlags);
				},
				true
			);

			if (DDC.receiveTrackingEvent) {
				// widgetUserEvents are published through trackEvent and can come with optionalData that we want included
				DDC.receiveTrackingEvent(
					(event, eventData = {}, optionalData = {}) => {
						if (
							isObject(eventData) &&
							eventData.event &&
							eventData.event === 'widgetUserEvent'
						) {
							const eventDataForListener = {
								...eventData,
								...(isObject(optionalData) && {
									...optionalData
								})
							};

							callListener({
								event: eventData.event,
								eventData: eventDataForListener
							});

							// docs/debug.md
							window.dispatchEvent(
								new CustomEvent('wsTaggingWUE', {
									detail: eventDataForListener
								})
							);
						}
					}
				);
			}

			if (window.DDC?.dataLayer?.page?.pageInfo?.isVlp) {
				await initVehicleShown(window);
			}

			if (DDC.WidgetData['ws-tracking']?.ddctrackingMessages?.forEach) {
				DDC.WidgetData['ws-tracking']?.ddctrackingMessages.forEach(
					(event) => {
						callListener({
							event: 'ddc.message',
							eventData: event.data
						});
					}
				);
			} else {
				window?.newrelic?.addPageAction?.(
					'wsTaggingTrackingMessagesNotFound',
					{
						location: '1'
					}
				);
			}

			window.addEventListener('ddcTrackingData', ({ detail }) => {
				callListener({
					event: 'ddc.message',
					eventData: detail
				});
			});

			window.coxAutoDataLayer?.subscribe?.(
				({ state: _state }, eventData) => {
					callListener({
						event: 'commonDataLayerEvent',
						eventData
					});
				}
			);
		}
	}
};

let initialized = false;

const setInitialized = (value) => {
	initialized = value;
};

const setFlags = (flagsArg) => {
	flags = flagsArg;
};

/**
 * @description This method ensures there is ws-tagging data on the page before running the init() function
 */
export const initIfWsTaggingReady = (flagsArg) => {
	if (!initialized) {
		flags = flagsArg;
		if (window?.DDC?.WidgetData?.['ws-tagging']?.mapping) {
			init({
				flags: flagsArg
			});
		} else {
			window.addEventListener('ws-tagging-ready', () => {
				init({
					flags: flagsArg
				});
			});
		}
		initialized = true;
	}
};

// only export if the test framework is running
export const exportedForTesting = {
	...(typeof test !== 'undefined' && {
		MappingConfiguration,
		mappingConfigurations,
		callListener,
		setFlags,
		setMappingConfigurations,
		init,
		setInitialized
	})
};
